import { REALMS } from "@/constants/process";
import { BaseRepositoryV2 } from "@/repositories/base-repository-v2";
import type {
  CMRepositoryMethods,
  CvRequired,
  Document,
  GetTestInstructionsResponseType,
  UploadAnswersBody,
} from "@/repositories/cv/types";
import type { Test } from "@/repositories/process/types";
import type { RealmSliceState } from "@/store/realm-slice";

export default class CMRepository extends BaseRepositoryV2 implements CMRepositoryMethods {
  // It's important to declare every method here and assign them as arrow functions
  // in the constructor to avoid errors in the client components that use them.
  path: string;

  getPrivacyPolicies;
  getTermsAndConditions;
  getTestInstructions;
  getCvRequired;
  uploadAnswers;

  constructor() {
    super();
    this.path = "/cm";

    this.getPrivacyPolicies = async () => {
      const response = await this.get(`${this.path}/parameters/privacy-policies`, {}, true);
      return this.toAxiosResponse<Document>(response);
    };

    this.getTermsAndConditions = async () => {
      const response = await this.get(`${this.path}/parameters/terms-and-conditions`, {}, true);
      return this.toAxiosResponse<Document>(response);
    };

    /**
     * @abstract Makes a GET request to retrieve test instructions for a specific test.
     * @param type - The type of test.
     * @param testId - The unique identifier of the test.
     * @param realm - The realm to use for the request.
     * @returns A promise that resolves to test instructions data.
     * @throws If the request fails.
     */
    this.getTestInstructions = async (
      testId: Test["id"],
      type: string,
      realm: RealmSliceState["realm"],
      language: string
    ) => {
      const response = await this.get(
        `${this.path}/tests/${type}/${testId}/instructions`,
        {
          "x-provider-realm": realm || REALMS.EVCORE,
          "Accept-Language": language,
        },
        true
      );
      return this.toAxiosResponse<GetTestInstructionsResponseType>(response);
    };

    /**
     * Makes a GET request to retrieve CV required status for a specific process.
     * @param processId - The unique identifier of the process.
     * @returns A promise that resolves to the CV required status.
     * @throws If the request fails.
     */
    this.getCvRequired = async (processId: string) => {
      const coreRepo = new BaseRepositoryV2(
        `${typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_CORE_URL : ""}/cm/process`
      );
      const response = await coreRepo.get(`${processId}/cv/required`, {}, true);
      return coreRepo.toAxiosResponse<CvRequired>(response);
    };
    this.uploadAnswers = async (body: UploadAnswersBody) => {
      const coreRepo = new BaseRepositoryV2(
        `${typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_CORE_URL : ""}/evaluation-web`
      );
      const response = await coreRepo.post("/answers/cv", body, {}, true);
      return coreRepo.toAxiosResponse(response);
    };
  }
}
